import {groupByUniqueKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React from 'react';
import * as uuid from 'uuid';

import BenchlingConfig from 'BenchlingConfig';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartSchemaMapperConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, RED} from 'ui-library/Colors';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import ReorderableSimpleTableRow, {
  ReorderableSimpleTableRowProps,
} from 'ui-library/reorderable/ReorderableSimpleTableRow';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import ConfigureMapperButton from './ConfigureMapperButton';
import {ClientSchemaMapperConfig} from './FlowchartConfigConstants';
import MultiSelectTaskSchemasDropdown from './MultiSelectTaskSchemasDropdown';
import SelectTaskSchemaDropdown from './SelectTaskSchemaDropdown';

export interface SchemaMapperConfigsPaneProps {
  isEditing: boolean;
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  allSchemasById: SchemaById;
  didInitialConfigHaveConditionalMapping: boolean;
  onChange: (schemaMapperConfigs: Array<ClientSchemaMapperConfig>) => void;
}

function setFromSchemas(
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  idx: number,
  fromTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>
): Array<ClientSchemaMapperConfig> {
  return produce(schemaMapperConfigs, (draftSchemaMapperConfigs) => {
    draftSchemaMapperConfigs[idx].fromTaskSchemas = fromTaskSchemas;
    draftSchemaMapperConfigs[idx].mapperConfig = null;
    return draftSchemaMapperConfigs;
  });
}

function setToSchema(
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  idx: number,
  toTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
): Array<ClientSchemaMapperConfig> {
  return produce(schemaMapperConfigs, (draftSchemaMapperConfigs) => {
    draftSchemaMapperConfigs[idx].toTaskSchema = toTaskSchema;
    draftSchemaMapperConfigs[idx].mapperConfig = null;
    return draftSchemaMapperConfigs;
  });
}

function setMappings(
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  idx: number,
  mapperConfig: ProcessFlowchartSchemaMapperConfigSpec
): Array<ClientSchemaMapperConfig> {
  return produce(schemaMapperConfigs, (draftSchemaMapperConfigs) => {
    draftSchemaMapperConfigs[idx].mapperConfig = mapperConfig;
    return draftSchemaMapperConfigs;
  });
}

function removeMapperConfig(
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  idx: number
): Array<ClientSchemaMapperConfig> {
  return produce(schemaMapperConfigs, (draftSchemaMapperConfigs) => {
    draftSchemaMapperConfigs.splice(idx, 1);
  });
}

// TODO(BNCH-41888): Surface error when user has configured multiple mappers from the same set of sources.
export default function SchemaMapperConfigPane(props: SchemaMapperConfigsPaneProps): JSX.Element {
  const taskSchemasById = groupByUniqueKey(
    [props.rootTaskSchema, ...props.availableTaskSchemas],
    ({id}) => id
  );

  const getReorderableRowItem = (
    rowItem: ClientSchemaMapperConfig,
    idx: number
  ): ItemData<ReorderableSimpleTableRowProps> => ({
    id: rowItem.clientId,
    itemClass: ReorderableSimpleTableRow,
    itemProps: {
      isDraggingDisabled: !props.isEditing,
      dragHandleClassName: css(styles.dragHandle),
      row: {
        id: rowItem.clientId,
        cells: [
          {
            key: `${rowItem.clientId}-fromTaskSchemas`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                <MultiSelectTaskSchemasDropdown
                  taskSchemas={rowItem.fromTaskSchemas}
                  possibleTaskSchemas={[props.rootTaskSchema, ...props.availableTaskSchemas]}
                  onSelectTaskSchemaIds={(ids) => {
                    const fromTaskSchemas = ids.map((id) => taskSchemasById[id]);
                    props.onChange(setFromSchemas(props.schemaMapperConfigs, idx, fromTaskSchemas));
                  }}
                  disabled={
                    BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1 || !props.isEditing
                  }
                  dataTestAttrs={{
                    component: 'SchemaMapperConfigsPane',
                    element: 'fromTaskSchemasDropdown',
                    key: `${idx}`,
                  }}
                />
              </div>
            ),
          },
          {
            key: `${rowItem.clientId}-toTaskSchema`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                <SelectTaskSchemaDropdown
                  taskSchema={rowItem.toTaskSchema}
                  possibleTaskSchemas={
                    props.rootTaskSchema.outputSchema != null
                      ? [props.rootTaskSchema, ...props.availableTaskSchemas]
                      : props.availableTaskSchemas
                  }
                  onSelectTaskSchemaId={(id) => {
                    props.onChange(setToSchema(props.schemaMapperConfigs, idx, taskSchemasById[id]));
                  }}
                  isLink={false}
                  disabled={
                    BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1 || !props.isEditing
                  }
                  dataTestAttrs={{
                    component: 'SchemaMapperConfigsPane',
                    element: 'toTaskSchemaDropdown',
                    key: `${idx}`,
                  }}
                />
              </div>
            ),
          },
          {
            key: `${rowItem.clientId}-configureMapper`,
            contents: (
              <ConfigureMapperButton
                schemaMapperConfig={rowItem}
                rootTaskSchema={props.rootTaskSchema}
                onSetMapperConfig={(mapperConfigSpec) => {
                  if (mapperConfigSpec == null) {
                    return;
                  }
                  props.onChange(setMappings(props.schemaMapperConfigs, idx, mapperConfigSpec));
                }}
                allSchemasById={props.allSchemasById}
                allTaskSchemas={props.allTaskSchemas}
                isEditing={props.isEditing}
                didInitialConfigHaveConditionalMapping={props.didInitialConfigHaveConditionalMapping}
                dataTestAttrs={{
                  component: 'SchemaMapperConfigsPane',
                  element: 'configureMapperButton',
                  key: `${idx}`,
                }}
              />
            ),
          },
          ...(props.isEditing
            ? [
                {
                  key: `${rowItem.clientId}-delete`,
                  cellClassName: 'simpleDivTable-actionGroupContainer',
                  contents: (
                    <div className={css(styles.buttonContainer)}>
                      <TooltipWrapper tooltipTitle="Remove from flowchart config">
                        <Button
                          buttonStyle={ButtonStyle.LINK}
                          className={classNames(css(styles.singleButton), css(styles.deleteButton))}
                          icon="delete"
                          onClick={() => {
                            props.onChange(removeMapperConfig(props.schemaMapperConfigs, idx));
                          }}
                          dataTestAttrs={{
                            component: 'SchemaMapperConfigsPane',
                            element: 'removeMapperButton',
                          }}
                        />
                      </TooltipWrapper>
                    </div>
                  ),
                },
              ]
            : []),
        ],
      },
    },
  });

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'SchemaMapperConfigsPane', element: 'root'})}
    >
      {props.isEditing && (
        <Button
          className={classNames(css(styles.singleButton), css(styles.addButton))}
          textClassName={css(styles.addButtonText)}
          buttonStyle={ButtonStyle.LINK}
          onClick={() => {
            props.onChange([
              ...props.schemaMapperConfigs,
              {
                clientId: uuid.v4(),
                mapperConfig: null,
                fromTaskSchemas: [],
                toTaskSchema: null,
              },
            ]);
          }}
          icon="compose"
          text="Add transition"
          dataTestAttrs={{
            component: 'SchemaMapperConfigsPane',
            element: 'addMapperButton',
          }}
        />
      )}
      {props.schemaMapperConfigs.length === 0 ? (
        <div>No transitions added to flowchart configuration.</div>
      ) : (
        <div className={css(styles.listWrapper)}>
          <div className={classNames('simpleDivTable-header', css(styles.selectItemContainer))}>
            <div className="simpleDivTable-headerCell simpleDivTable-header--title">Source</div>
            <div className="simpleDivTable-headerCell simpleDivTable-header--title">Target</div>
            <div className="simpleDivTable-headerCell simpleDivTable-header--title">Mapping</div>
          </div>
          <ReorderableList
            itemDatas={props.schemaMapperConfigs.map((mapperConfig, idx) =>
              getReorderableRowItem(mapperConfig, idx)
            )}
            onMoveItem={(rowIds) => {
              const schemaMapperConfigsByClientId = groupByUniqueKey(
                props.schemaMapperConfigs,
                ({clientId}) => clientId
              );
              props.onChange(rowIds.map((rowId) => schemaMapperConfigsByClientId[rowId]));
            }}
            onMoveItemEnd={() => {}}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    margin: '16px 0',
  },
  listWrapper: {
    border: `1px solid ${GRAY_LIGHT}`,
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mappingColumnHeader: {paddingLeft: '50px'},
  dropdownContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  dragHandle: {
    marginRight: 0,
    width: '16px',
  },
  buttonContainer: {
    marginLeft: '15px',
  },
  singleButton: {
    padding: '0px',
    boxShadow: 'none',
  },
  addButton: {
    marginBottom: '16px',
    marginTop: '-7px',
  },
  addButtonText: {
    marginLeft: '4px',
  },
  deleteButton: {
    paddingLeft: '8px',
    paddingRight: '8px',
    color: GRAY,
    ':hover': {
      color: RED,
    },
  },
});
